<template>
  <div v-if="user">
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h2 class="mb-0">
        Créer un nouveau colis
      </h2>
    </div>

    <!-- Supplier Statistics Card - Enhanced Professional Design -->
    <transition name="slide-fade">
      <b-card
        v-if="supplierStats && showStats"
        class="mb-2 stats-card"
        no-body
      >
        <!-- Elegant Header with Gradient -->
        <div class="stats-header">
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div class="stats-icon-wrapper mr-1">
                <feather-icon
                  icon="TrendingUpIcon"
                  size="28"
                />
              </div>
              <div>
                <h3 class="mb-0 text-white font-weight-bold">
                  Historique du Destinataire
                </h3>
                <div class="d-flex align-items-center mt-50">
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                    class="text-white-50 mr-50"
                  />
                  <span
                    class="text-white font-weight-semibold"
                    style="font-size: 0.95rem;"
                  >
                    {{ supplierStats.phone_number }}
                  </span>
                </div>
              </div>
            </div>
            <b-badge
              :variant="supplierStats.is_serious ? 'success' : 'warning'"
              pill
              class="stats-badge"
            >
              <feather-icon
                :icon="supplierStats.is_serious ? 'CheckCircleIcon' : 'AlertCircleIcon'"
                size="16"
                class="mr-50"
              />
              <span style="font-size: 1rem;">{{ supplierStats.serious_status_display }}</span>
            </b-badge>
          </div>
        </div>

        <b-card-body class="p-2">
          <!-- Enhanced Key Metrics Row -->
          <b-row class="match-height">
            <!-- Total Records -->
            <b-col
              lg="3"
              sm="6"
              class="mb-1"
            >
              <div class="metric-card metric-info">
                <div class="metric-icon bg-info">
                  <feather-icon
                    icon="PackageIcon"
                    size="24"
                  />
                </div>
                <div class="metric-content">
                  <h2 class="metric-value text-info">
                    {{ supplierStats.total_records }}
                  </h2>
                  <p class="metric-label text-info">
                    Total Colis
                  </p>
                </div>
              </div>
            </b-col>

            <!-- Delivered -->
            <b-col
              lg="3"
              sm="6"
              class="mb-1"
            >
              <div class="metric-card metric-success">
                <div class="metric-icon bg-success">
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="24"
                  />
                </div>
                <div class="metric-content">
                  <h2 class="metric-value text-success">
                    {{ supplierStats.total_livre }}
                  </h2>
                  <p class="metric-label text-success">
                    Livrés
                  </p>
                </div>
              </div>
            </b-col>

            <!-- Returned -->
            <b-col
              lg="3"
              sm="6"
              class="mb-1"
            >
              <div class="metric-card metric-danger">
                <div class="metric-icon bg-danger">
                  <feather-icon
                    icon="XCircleIcon"
                    size="24"
                  />
                </div>
                <div class="metric-content">
                  <h2 class="metric-value text-danger">
                    {{ supplierStats.total_retour }}
                  </h2>
                  <p class="metric-label text-danger">
                    Retournés
                  </p>
                </div>
              </div>
            </b-col>

            <!-- Success Rate -->
            <b-col
              lg="3"
              sm="6"
              class="mb-1"
            >
              <div
                class="metric-card"
                :class="supplierStats.success_rate >= 70 ? 'metric-success-rate' : 'metric-warning-rate'"
              >
                <div
                  class="metric-icon"
                  :class="supplierStats.success_rate >= 70 ? 'bg-success' : 'bg-warning'"
                >
                  <feather-icon
                    icon="TrendingUpIcon"
                    size="24"
                  />
                </div>
                <div class="metric-content">
                  <h2
                    class="metric-value"
                    :class="supplierStats.success_rate >= 70 ? 'text-success' : 'text-warning'"
                  >
                    {{ supplierStats.success_rate }}%
                  </h2>
                  <p
                    class="metric-label"
                    :class="supplierStats.success_rate >= 70 ? 'text-success' : 'text-warning'"
                  >
                    Taux de Réussite
                  </p>
                </div>
              </div>
            </b-col>
          </b-row>

        </b-card-body>
      </b-card>
    </transition>

    <b-card-code title="Formulaire de création">
      <!-- Removed extra instructional text -->
      <div
        v-if="isLoading === true"
        class="text-center mb-2"
      >
        <b-spinner
          variant="primary"
          label="Text Centered"
        />
      </div>
      <!-- form -->
      <validation-observer ref="simpleRules">
        <b-form>
          <b-row class="mt-1">
            <!-- Phone number input as first field -->
            <b-col md="4">
              <b-form-group
                label="Téléphone du destinataire *"
                label-for="Téléphone"
                description="Numéro de téléphone principal du destinataire (8 chiffres)."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Téléphone"
                  rules="required|tnPhone"
                >
                  <b-form-input
                    v-model="order.phone1"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Téléphone principal du destinataire"
                    maxlength="8"
                    minlength="8"
                    type="tel"
                    :formatter="formatPhone"
                    autocomplete="off"
                    autofocus
                    @keypress="if (!/\d/.test($event.key)) $event.preventDefault()"
                    @input="order.phone1 = order.phone1.replace(/[^\d]/g, '').slice(0,8); onPhoneInput()"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <!-- Nom du destinataire -->
            <b-col md="4">
              <b-form-group
                label="Nom du destinataire *"
                label-for="nom"
                description="Nom complet du destinataire du colis."
              >
                <validation-provider
                  #default="{ errors }"
                  name="nom"
                  rules="required"
                >
                  <b-form-input
                    v-model="order.name"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Nom du destinataire"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Délégation de destination *"
                label-for="delegation"
                description="Sélectionnez la délégation de destination."
              >
                <validation-provider
                  #default="{ errors }"
                  name="delegation"
                  rules="required"
                >
                  <v-select
                    v-model="order.delegation"
                    :clearable="true"
                    placeholder="Choisissez une délégation de destination"
                    label="name"
                    :options="delegations"
                    @input="onDelegationInput"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group
                label="Gouvernorat de destination *"
                label-for="gouvernorat"
                description="Rempli automatiquement selon la délégation sélectionnée."
              >
                <validation-provider
                  #default="{ errors }"
                  name="gouvernorat"
                  rules="required"
                >
                  <v-select
                    v-model="order.governorate"
                    :clearable="true"
                    placeholder="Choisissez ou confirmez le gouvernorat"
                    label="name"
                    :options="governorates"
                    @input="onGovernorateInput"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Localité de destination"
                label-for="localité"
                description="Sélectionnez la localité de destination."
              >
                <validation-provider
                  #default="{ errors }"
                  name="localité"
                >
                  <v-select
                    v-model="order.locality"
                    :clearable="false"
                    placeholder="Choisissez une localité de destination"
                    label="name"
                    :options="localities"
                    :disabled="!order.delegation"
                    @input="onLocalityInput"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group
                label="Adresse complète de livraison *"
                label-for="adresse"
                description="Adresse complète pour la livraison du colis."
              >
                <validation-provider
                  #default="{ errors }"
                  name="adresse"
                  rules="required"
                >
                  <b-form-input
                    v-model="order.address"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Adresse complète de livraison"
                    @input="onAddressInput"
                  />
                  <div
                    v-if="detectedLocation || isDetectingAddress"
                    class="detected-location mt-1"
                  >
                    <div class="detected-location__title">
                      Detected Location
                    </div>
                    <div
                      v-if="isDetectingAddress"
                      class="text-muted"
                    >
                      ...
                    </div>
                    <div
                      v-else-if="detectedLocation"
                      class="detected-location__content"
                    >
                      <div v-if="detectedLocation.governorate">
                        ✓ Governorate: {{ detectedLocation.governorate.name }}
                      </div>
                      <div v-if="detectedLocation.delegation">
                        ✓ Delegation: {{ detectedLocation.delegation.name }}
                      </div>
                      <div v-if="detectedLocation.locality">
                        ✓ Locality: {{ detectedLocation.locality.name }}
                      </div>
                      <div class="text-muted">
                        Confidence: {{ detectedLocation.confidence }}%
                      </div>
                    </div>
                  </div>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <!-- ...existing code... -->
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Téléphone secondaire"
                label-for="Téléphone2"
                description="Numéro de téléphone secondaire du destinataire (optionnel)."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Telephone2"
                  rules="tnPhone"
                >
                  <b-form-input
                    v-model="order.phone2"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Téléphone secondaire du destinataire"
                    maxlength="8"
                    minlength="8"
                    type="tel"
                    :formatter="formatPhone"
                    autocomplete="off"
                    @keypress="if (!/\d/.test($event.key)) $event.preventDefault()"
                    @input="order.phone2 = order.phone2.replace(/[^\d]/g, '').slice(0,8)"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group
                label="Montant total TTC *"
                label-for="montant ttc"
                description="Montant total à payer, toutes taxes comprises."
              >
                <validation-provider
                  #default="{ errors }"
                  name="montant ttc"
                  rules="required|priceDecimal"
                >
                  <b-form-input
                    v-model="order.price_ttc"
                    :formatter="formatPrice"
                    type="text"
                    inputmode="decimal"
                    placeholder="Montant total TTC"
                    :state="(errors.length > 0 || serverErrors.price_ttc || clientErrors.price_ttc) ? false : null"
                    @keypress="allowOnlyDecimalNumbers"
                    @input="validatePriceTTC"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="clientErrors.price_ttc"
                    class="text-danger"
                  >{{ clientErrors.price_ttc }}</small>
                  <small
                    v-if="serverErrors.price_ttc"
                    class="text-danger"
                  >{{ sanitizeError(firstError(serverErrors.price_ttc)) }}</small>
                  <small class="text-muted d-block">
                    Maximum autorisé :
                    <span v-if="maxOrderPriceTtc !== null">{{ maxOrderPriceTtcLabel }} TND</span>
                    <span v-else>sans limite</span>
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col
              v-if="user.weight_price === true"
              md="4"
            >
              <b-form-group
                label="Poids volumétrique du colis *"
                label-for="Poids volumétrique de colis"
                description="Sélectionnez le poids volumétrique du colis pour le calcul logistique."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Poids de colis"
                  rules="required"
                >
                  <v-select
                    v-model="order.weight"
                    :clearable="false"
                    placeholder="Poids volumétrique du colis"
                    label="weight"
                    :options="user.weights"
                    @input="fetch_price"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Mode de paiement du destinataire *"
                label-for="type"
                description="Sélectionnez le mode de paiement utilisé par le destinataire."
              >
                <validation-provider
                  #default="{ errors }"
                  name="type"
                  rules="required"
                >
                  <v-select
                    v-model="order.payment_method"
                    :clearable="false"
                    placeholder="Mode de paiement du destinataire"
                    label="value"
                    :options="types"
                    :reduce="type => type.key"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Options du colis"
                label-for="package-options"
                description="Sélectionnez les options applicables au colis."
              >
                <div class="d-flex flex-wrap align-items-center">
                  <b-form-checkbox
                    v-model="order.is_an_exchange"
                    name="check-button"
                    switch
                    inline
                    class="mr-2 mb-1"
                  >
                    <feather-icon
                      icon="RefreshCwIcon"
                      size="18"
                      class="mr-50"
                      style="color: #5A8DEE;"
                    />
                    <span style="font-size: 0.95rem;">Échange</span>
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="order.is_fragile"
                    name="check-button"
                    switch
                    inline
                    class="mr-2 mb-1"
                  >
                    <feather-icon
                      icon="AlertTriangleIcon"
                      size="18"
                      class="mr-50"
                      style="color: #FDAC41;"
                    />
                    <span style="font-size: 0.95rem;">Fragile</span>
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="order.is_discharge"
                    name="check-button"
                    switch
                    inline
                    class="mr-2 mb-1"
                  >
                    <feather-icon
                      icon="FileTextIcon"
                      size="18"
                      class="mr-50"
                      style="color: #A3A1FB;"
                    />
                    <span style="font-size: 0.95rem;">Décharge</span>
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="order.can_open_package"
                    name="check-button"
                    switch
                    inline
                    class="mb-1"
                  >
                    <feather-icon
                      icon="PackageIcon"
                      size="18"
                      class="mr-50"
                      style="color: #C59C6C;"
                    />
                    <span style="font-size: 0.95rem;">Ouverture</span>
                  </b-form-checkbox>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <br>
          <b-col v-if="order.is_an_exchange">
            <b-form-group
              label="Article à échanger (optionnel)"
              label-for="exchange_article"
              description="Nom ou référence de l’article à échanger."
            >
              <validation-provider
                v-slot="{ errors }"
                name="exchange_article"
              >
                <b-form-input
                  id="exchange_article"
                  v-model="order.exchange_article"
                  placeholder="Article à échanger"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col v-if="order.is_an_exchange">
            <b-form-group
              label="Quantité à échanger (optionnel)"
              label-for="exchange_quantity"
              description="Quantité de l’article à échanger."
            >
              <validation-provider
                v-slot="{ errors }"
                name="exchange_quantity"
              >
                <b-form-input
                  id="exchange_quantity"
                  v-model="order.exchange_quantity"
                  placeholder="Quantité à échanger"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-card-code title="Produits">
            <b-card-text>
              <span>Veuillez saisir les informations logistiques des produits à livrer.</span>
            </b-card-text>
            <!-- form -->
            <validation-observer ref="simpleRules">
              <div>
                <div>
                  <b-form
                    ref="form"
                    :style="{ height: trHeight }"
                    class="repeater-form"
                    @submit.prevent="repeateAgain"
                  >
                    <!-- Row Loop -->
                    <b-row
                      v-for="(item, index) in items"
                      :id="item.id"
                      :key="item.id"
                      ref="row"
                    >
                      <!-- produit -->
                      <b-col md="6">
                        <b-form-group
                          label="Désignation du produit"
                          label-for="produit"
                          description="Nom ou désignation du produit à livrer."
                        >
                          <validation-provider
                            #default="{ errors }"
                            name="Désignation du produit"
                            rules="required"
                          >
                            <b-form-input
                              v-model="item.product"
                              placeholder="Désignation du produit"
                              :state="errors.length > 0 ? false : null"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-form-group>
                      </b-col>
                      <!-- Quantity -->
                      <!-- <b-col md="3">
                        <b-form-group
                        label="Quantité"
                        label-for="quantity"
                        description="Quantité du produit à livrer."
                        >
                          <b-form-input
                            id="quantity"
                            v-model="item.quantity"
                            type="number"
                          placeholder="Quantité"
                          />
                        </b-form-group>
                      </b-col> -->
                      <b-col md="3">
                        <b-form-group
                          label="Quantité"
                          label-for="quantity"
                        >
                          <validation-provider
                            #default="{ errors }"
                            name="quantity"
                            rules="required"
                          >
                            <b-form-input
                              id="quantity"
                              v-model="item.quantity"
                              type="number"
                              placeholder="Quantity"
                              :state="errors.length > 0 ? false : null"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-form-group>
                      </b-col>
                      <!-- Remove Button -->
                      <b-col
                        lg="2"
                        md="6"
                        class="mb-50"
                      >
                        <b-button
                          v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                          variant="outline-danger"
                          class="mt-0 mt-md-2"
                          @click="removeItem(index)"
                        >
                          <feather-icon
                            icon="XIcon"
                            class="mr-25"
                          />
                          <span>Supprimer</span>
                        </b-button>
                      </b-col>
                      <b-col cols="12">
                        <hr>
                      </b-col>
                    </b-row>
                  </b-form>
                </div>
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  class="mb-2"
                  variant="outline-secondary"
                   :style="{
                     backgroundColor: 'rgba(17, 64, 146, 0.1) !important',
                     borderColor: 'rgba(17, 64, 146, 0.2) !important',
                     color: '#114092 !important'
                   }"
                  @click="repeateAgain"
                >
                  <feather-icon
                    icon="PlusIcon"
                    class="mr-25"
                  />
                  <span>Nouveau produit</span>
                </b-button>
                <b-row>
                  <b-col md="12">
                    <b-form-group
                      label="Commentaire (optionnel)"
                      label-for="Commentaire"
                      description="Ajoutez un commentaire ou une instruction spéciale pour la livraison."
                    >
                      <b-form-textarea
                        id="textarea-rows"
                        v-model="order.note"
                        placeholder="Commentaire ou instruction spéciale"
                        rows="1"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="12">
                    <span id="create-order-btn-wrapper">
                      <b-button
                        variant="primary"
                        type="submit"
                        class="mb-1 mb-sm-0 mr-0 mr-sm-1 mt-3"
                        :class="{ 'disabled-cursor': isLoading || !isOrderValid || items.some(item => !item.product) || order.address === '' || !order.delegation }"
                        :disabled="isLoading || !isOrderValid || items.some(item => !item.product) || order.address === '' || !order.delegation "
                        @click.prevent="addOrder"
                      >
                        <feather-icon
                          icon="SaveIcon"
                          class="mr-1"
                        />
                        {{ isLoading ? 'Création...' : 'Créer le colis' }}
                      </b-button>
                      <b-tooltip
                        v-if="!isOrderValid || items.some(item => !item.product) || order.address === '' || !order.delegation"
                        target="create-order-btn-wrapper"
                        triggers="hover focus"
                        placement="top"
                      >
                        <template v-if="order.name === ''">
                          Saisissez le nom du destinataire pour activer la création.
                        </template>
                        <template v-else-if="order.phone1 === ''">
                          Saisissez le téléphone du destinataire pour activer la création.
                        </template>
                        <template v-else-if="order.price_ttc === ''">
                          Saisissez le montant total TTC pour activer la création.
                        </template>
                        <template v-else-if="!order.governorate">
                          Sélectionnez le gouvernorat de destination pour activer la création.
                        </template>
                        <template v-else-if="order.address === ''">
                          Saisissez l'adresse de livraison pour activer la création.
                        </template>
                        <template v-else-if="!order.delegation">
                          Le champ délégation est obligatoire pour activer la création.
                        </template>
                        <template v-else-if="items.some(item => !item.product)">
                          Veuillez saisir au moins un produit pour activer la création.
                        </template>
                        <template v-else>
                          Remplissez tous les champs obligatoires pour activer la création.
                        </template>
                      </b-tooltip>
                    </span>
                    <b-button
                      variant="outline-secondary"
                      type="reset"
                      class="mt-3"
                      @click.prevent="clearForm"
                    >
                      <b-icon
                        icon="arrow-counterclockwise"
                        class="mr-2"
                      />
                      Réinitialiser
                    </b-button>
                  </b-col>
                </b-row>
              </div>
            </validation-observer>
          </b-card-code>
        </b-form>
      </validation-observer>
    </b-card-code>

    <div>
      <!-- Client Search Fields -->
      <b-form-group
        label="Chercher client par téléphone ou nom"
        label-for="client-search"
      >
        <b-input-group>
          <b-form-input
            v-model.trim="clientPhone"
            type="tel"
            placeholder="Téléphone du client"
            maxlength="8"
            minlength="8"
            @blur="fetchClientStats"
          />
          <b-form-input
            v-model.trim="clientName"
            placeholder="Nom du client"
            @blur="fetchClientStats"
          />
        </b-input-group>
      </b-form-group>

      <!-- Client Statistics -->
      <div v-if="clientStats">
        <h3>Statistiques d'achat du client</h3>
        <p>Total des commandes: {{ clientStats.total_orders }}</p>
        <p>Montant total dépensé: {{ clientStats.total_spent }}</p>
        <p>Date de la dernière commande: {{ clientStats.last_order_date }}</p>
        <ul>
          <li
            v-for="clientOrder in clientStats.orders"
            :key="clientOrder.id"
          >
            {{ clientOrder.created_at }} - {{ clientOrder.price_ttc }} - {{ clientOrder.status }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import BCardCode from '@core/components/b-card-code'
import vSelect from 'vue-select'
import { heightTransition } from '@core/mixins/ui/transition'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import storeAuth from '@/store/store'

/* import vSelect from 'vue-select' */
import {
  BFormInput,
  BFormGroup,
  BForm,
  BRow,
  BCol,
  BButton,
  BFormTextarea,
  BCardText /*  */,
  BFormCheckbox,
} from 'bootstrap-vue'
import { required } from '@validations'

export default {
  name: 'CreateOrder',
  components: {
    BCardCode,
    ValidationProvider,
    ValidationObserver,
    BCardText,
    /* BFormTextarea, */
    BFormInput,
    BFormGroup,
    BForm,
    BRow,
    BCol,
    BButton,
    BFormTextarea,
    vSelect,
    BFormCheckbox,
  },
  directives: {
    Ripple,
  },
  mixins: [heightTransition],
  data() {
    return {
      items: [
        {
          product: '',
          quantity: 1,
        },
      ],
      itemsOptions: [],
      nextTodoId: 1,
      types: [
        {
          key: 0,
          value: 'Espèce',
        },
        {
          key: 1,
          value: 'par chèque',
        },
        {
          key: 2,
          value: 'par traite',
        },
      ],
      governorates: [],
      delegations: [],
      localities: [],
      detectedLocation: null,
      isDetectingAddress: false,
      addressDetectionTimer: null,
      addressDetectionRequestId: 0,
      manualLocationOverride: false,
      order: {
        payment_method: 0,
        name: '',
        address: '',
        phone1: '',
        phone2: '',
        note: '',
        price_ttc: '',
        price_delivery: '',
        governorate: '',
        delegation: '',
        exchange_article: '',
        exchange_quantity: '',
        locality: null,
        is_an_exchange: false,
        is_fragile: false,
        is_discharge: false,
        can_open_package: false,
        total_boxes: 1,
      },
      required,
      user: null,
      isLoading: false,
      isHasManyBoxes: false,
      // phoneNumber removed, now using order.phone1 only
      customer: null,
      totalOrders: null,
      pendingOrders: null,
      totalDeliveredOrders: null,
      totalReturnedOrders: null,
      successPercentage: null,
      showStats: false,
      clientPhone: '',
      clientName: '',
      clientStats: null,
      supplierStats: null,
      // Holds backend validation errors keyed by field name (e.g., price_ttc)
      serverErrors: {},
      // Holds client-side validation errors
      clientErrors: {},
    }
  },
  computed: {
    maxOrderPriceTtc() {
      const configured = storeAuth.state.company?.max_order_price_ttc
      if (configured === null || configured === undefined || configured === '') return null
      const amount = Number(configured)
      return Number.isFinite(amount) && amount >= 0.001 ? amount : null
    },
    maxOrderPriceTtcLabel() {
      if (this.maxOrderPriceTtc === null) return 'Sans limite'
      return this.maxOrderPriceTtc.toLocaleString('fr-FR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })
    },
    isOrderValid() {
      return (
        this.order.name !== ''
        && this.order.phone1 !== ''
        && this.order.price_ttc !== ''
        && !!this.order.governorate
        // && this.order.delegation !== ''
      )
    },
  },

  created() {
    storeAuth.dispatch('fetchCompanyInfo')
    this.getUser()
    this.getGovernorates()
    this.getDelegations()
  },
  beforeDestroy() {
    if (this.addressDetectionTimer) {
      clearTimeout(this.addressDetectionTimer)
    }
  },
  methods: {
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && currentValue.includes('.')) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
    onAddressInput() {
      if (this.addressDetectionTimer) {
        clearTimeout(this.addressDetectionTimer)
      }

      const address = (this.order.address || '').trim()
      if (address.length < 4 || this.manualLocationOverride) {
        this.detectedLocation = null
        this.isDetectingAddress = false
        return
      }

      const requestId = this.addressDetectionRequestId + 1
      this.addressDetectionRequestId = requestId

      this.addressDetectionTimer = setTimeout(async () => {
        this.isDetectingAddress = true
        try {
          const { data } = await axios.post('/api/address/detect/', { address })
          if (requestId !== this.addressDetectionRequestId) return

          if (data && data.success) {
            this.detectedLocation = data
            await this.applyDetectedLocation(data)
          } else {
            this.detectedLocation = null
          }
        } catch (error) {
          if (requestId === this.addressDetectionRequestId) {
            this.detectedLocation = null
          }
        } finally {
          if (requestId === this.addressDetectionRequestId) {
            this.isDetectingAddress = false
          }
        }
      }, 500)
    },
    async applyDetectedLocation(result) {
      const governorate = result.governorate
        ? this.findOptionById(this.governorates, result.governorate.id) || result.governorate
        : null

      if (governorate) {
        this.order.governorate = governorate
        await this.getDelegations(governorate.id)
      }

      const delegation = result.delegation
        ? this.findOptionById(this.delegations, result.delegation.id) || result.delegation
        : null

      if (delegation) {
        this.order.delegation = delegation
        this.syncGovernorateFromDelegation(delegation)
        await this.getLocalities()
      }

      const locality = result.locality
        ? this.findOptionById(this.localities, result.locality.id) || result.locality
        : null

      if (locality) {
        this.order.locality = locality
      }
    },
    findOptionById(options, id) {
      return (options || []).find(option => Number(option.id) === Number(id))
    },
    async onGovernorateInput() {
      this.manualLocationOverride = true
      this.detectedLocation = null
      this.order.delegation = ''
      this.order.locality = ''
      this.localities = []
      await this.getDelegations(this.order.governorate ? this.order.governorate.id : null)
    },
    async onDelegationInput() {
      this.manualLocationOverride = true
      this.detectedLocation = null
      this.syncGovernorateFromDelegation(this.order.delegation)
      await this.getLocalities()
    },
    onLocalityInput() {
      this.manualLocationOverride = true
      this.detectedLocation = null
    },
    async onPhoneInput() {
      // When phone1 is 8 digits, fetch supplier data and fill form
      if (this.order.phone1 && this.order.phone1.length === 8) {
        await this.searchSupplierByPhone(this.order.phone1)
      }
    },
    async searchSupplierByPhone(phone) {
      try {
        this.isLoading = true
        this.supplierStats = null

        // Search for supplier by phone
        const { data } = await axios.post('/api/auth/suppliers/search-by-phone/', {
          phone_number: phone,
        })

        if (data) {
          // Fill form with supplier data
          this.order.name = data.name || ''
          this.order.address = data.address || ''
          this.order.phone2 = data.phone2 || ''

          // Set governorate and load delegations
          if (data.governorate) {
            this.order.governorate = data.governorate
            await this.getDelegations(data.governorate.id)
          }

          // Set delegation and load localities
          if (data.delegation) {
            this.order.delegation = data.delegation
            this.syncGovernorateFromDelegation(data.delegation)
            await this.getLocalities()
          }

          // Fetch statistics for this phone
          await this.fetchSupplierStatistics(phone)

          this.showToast('success', 'top-center', 'Données du destinataire chargées automatiquement')
        }
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        const status = error && error.response ? error.response.status : null
        if (status === 404) {
          // If no supplier found, try the old customer endpoint as fallback
          this.fetchAndFillCustomer(phone)
          return
        }
        const serverData = error && error.response ? error.response.data : null
        // eslint-disable-next-line no-console
        console.error('Error searching supplier by phone:', status, serverData || error)
        const msg = this.sanitizeError(
          (serverData && (serverData.detail || serverData.message)) || 'Erreur serveur lors de la recherche par téléphone',
        )
        this.showToast('danger', 'top-center', msg)
      }
    },
    async fetchSupplierStatistics(phone) {
      try {
        const { data } = await axios.post('/api/auth/suppliers/phone-statistics/', {
          phone_number: phone,
        })

        if (data) {
          this.supplierStats = data
          this.showStats = true
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching statistics:', error)
        this.supplierStats = null
        this.showStats = false
      }
    },
    validatePriceTTC() {
      // Reset client error first
      this.$set(this.clientErrors, 'price_ttc', null)
      const val = this.order.price_ttc
      // Treat empty as no error; required rule handles emptiness
      if (val === '' || val === null || typeof val === 'undefined') return
      const n = Number(val)
      if (Number.isNaN(n)) {
        this.$set(this.clientErrors, 'price_ttc', 'Le montant TTC doit être un nombre valide')
        return
      }
      if (n < 0) {
        this.$set(this.clientErrors, 'price_ttc', 'Le montant TTC ne doit pas être négatif')
        return
      }
      if (this.maxOrderPriceTtc !== null && n > this.maxOrderPriceTtc) {
        this.$set(
          this.clientErrors,
          'price_ttc',
          `Le montant TTC ne doit pas dépasser ${this.maxOrderPriceTtcLabel} TND`,
        )
      }
    },
    async fetchAndFillCustomer(phone) {
      try {
        this.isLoading = true
        const { data } = await axios.get('/api/customers/customer-stats/', {
          params: { phone },
        })
        if (data.customer) {
          const { customer } = data
          this.customer = customer
          this.order.name = customer.name
          this.order.phone1 = customer.phone1
          this.order.phone2 = customer.phone2
          this.order.address = customer.address

          // Set governorate and load delegations
          this.order.governorate = customer.governorate
          if (customer.governorate && customer.governorate.id) {
            await this.getDelegations(customer.governorate.id)
          }
          // Set delegation and load localities
          this.order.delegation = customer.delegation
          if (customer.delegation && customer.delegation.id) {
            this.syncGovernorateFromDelegation(customer.delegation)
            await this.getLocalities()
          }
          // Set locality
          this.order.locality = customer.locality

          this.showToast('success', 'top-center', 'Les informations du client ont été rempli avec succès')
        } else {
          this.showToast('info', 'top-center', 'Aucun client trouvé')
        }
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        this.showToast('error', 'top-center', 'Aucun client trouvé')
      }
    },
    async fetch_price() {
      const result = this.user.prices.find(element => element.weight.id === this.order.weight.id)
      this.order.price_delivery = result.delivery_price
      this.order.price_return = result.return_price
    },
    async getUser() {
      const response = await axios.get('api/v1/users/me/')
      storeAuth.commit('setUser',
        response.data)
      this.user = response.data
      this.order.price_delivery = this.user.price_delivery
      this.order.price_return = this.user.price_return
    },
    async getGovernorates() {
      const { data } = await axios.get('/api/governorates/')
      this.governorates = data
    },
    async getDelegations(governorateId = null) {
      const params = governorateId ? { governorate: governorateId } : {}
      const { data } = await axios.get('/api/delegations/', { params })
      this.delegations = data
    },
    async getLocalities() {
      this.order.locality = ''
      if (!this.order.delegation || !this.order.delegation.id) {
        this.localities = []
        return
      }
      const { data } = await axios.get('/api/localities/', { params: { delegation: this.order.delegation.id } })
      this.localities = data
    },
    syncGovernorateFromDelegation(delegation) {
      if (!delegation) {
        this.order.governorate = ''
        return
      }
      if (!delegation.governorate) return

      this.order.governorate = this.findOptionById(this.governorates, delegation.governorate.id)
        || delegation.governorate
    },
    repeateAgain() {
      this.items.push({
        product: '',
        quantity: 1,
      })

      this.$nextTick(() => {
        this.trAddHeight(this.$refs.row[0].offsetHeight)
      })
    },
    removeItem(index) {
      this.items.splice(index, 1)
      this.trTrimHeight(this.$refs.row[0].offsetHeight)
    },

    async addOrder() {
      const { simpleRules } = this.$refs
      const isValid = await simpleRules.validate()
      if (isValid) {
        // Clear previous server errors
        this.serverErrors = {}
        let weight = null
        if (this.user.weight_price === true) {
          weight = this.order.weight.id
        }
        try {
          this.isLoading = true
          await axios.post(
            '/api/orders/create/', {
              name: this.order.name,
              address: this.order.address,
              phone1: this.order.phone1,
              phone2: this.order.phone2,
              note: this.order.note,
              price_ttc: this.order.price_ttc,
              price_delivery: this.order.price_delivery,
              price_return: this.order.price_return,
              governorate: this.order.governorate.id,
              delegation: this.order.delegation.id,
              locality: this.order.locality ? this.order.locality.id : null,
              products: this.items,
              is_an_exchange: this.order.is_an_exchange,
              exchange_article: this.order.exchange_article,
              exchange_quantity: this.order.exchange_quantity,
              is_fragile: this.order.is_fragile,
              is_discharge: this.order.is_discharge,
              can_open_package: this.order.can_open_package,
              payment_method: this.order.payment_method,
              weight,
            },
          )
          this.clearForm()
          this.showToast('success', 'top-center', 'Colis Ajouté avec avec succès')
          window.scrollTo(0, 0)
          this.isLoading = false
        } catch (error) {
          // Capture backend validation errors (DRF format)
          this.serverErrors = (error && error.response && error.response.data) ? error.response.data : {}
          const priceErr = this.serverErrors.price_ttc ? this.firstError(this.serverErrors.price_ttc) : null
          const rawMsg = priceErr || (this.serverErrors.detail ? this.firstError(this.serverErrors.detail) : 'Erreur lors de la création du colis')
          const msg = this.sanitizeError(rawMsg)
          setTimeout(() => { this.showToast('danger', 'top-center', msg) }, 300)
          this.isLoading = false
        }
      } else {
        setTimeout(() => {
          this.showToast('danger', 'top-center', 'champ obligatoire ! vérifier votre donnée !')
        }, 1000)
      }
    },
    firstError(value) {
      if (Array.isArray(value)) return value[0]
      if (value && typeof value === 'object') {
        const first = Object.values(value)[0]
        return Array.isArray(first) ? first[0] : (first || 'Erreur')
      }
      return typeof value === 'string' ? value : 'Erreur'
    },
    sanitizeError(text) {
      if (!text) return ''
      let t = String(text)
      // Remove occurrences of field keys like price_ttc (with or without colon)
      t = t.replace(/\bprice_ttc\b:?/gi, '')
      // Collapse multiple spaces and trim separators
      t = t.replace(/[_]+/g, ' ')
      t = t.replace(/^[\s:;-]+/, '').replace(/[\s:;-]+$/, '')
      // Remove parentheses
      t = t.replace(/[()]/g, '')
      return t.trim()
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    formatPhone(e) {
      return String(e).substring(0, 8)
    },
    clearForm() {
      this.order = {
        payment_method: 0,
        name: '',
        address: '',
        phone1: '',
        phone2: '',
        note: '',
        price_ttc: '',
        price_delivery: '',
        price_return: '',
        governorate: '',
        delegation: '',
        exchange_quantity: '',
        exchange_article: '',
        locality: null,
        is_an_exchange: false,
        is_fragile: false,
        is_discharge: false,
        can_open_package: false,
        total_boxes: 1,
      }
      this.order.price_delivery = this.user.price_delivery
      this.order.price_return = this.user.price_return
      this.serverErrors = {}
      this.clientErrors = {}
      this.detectedLocation = null
      this.isDetectingAddress = false
      this.manualLocationOverride = false
      this.items = [
        {
          product: '',
          quantity: 1,
        },
      ]
      this.$refs.simpleRules.reset()
    },
    // searchCustomer removed, now handled by onPhoneInput
    async fetchClientStats() {
      if (!this.clientPhone && !this.clientName) return
      const params = new URLSearchParams()
      if (this.clientPhone) params.append('phone', this.clientPhone)
      if (this.clientName) params.append('name', this.clientName)
      try {
        const { data } = await axios.get(`/api/orders/client-purchase-stats/?${params.toString()}`)
        this.clientStats = data
      } catch (error) {
        this.clientStats = null
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style lang="scss" scoped>
.repeater-form {
  overflow: hidden;
  transition: 0.35s height;
}

.disabled-cursor {
  cursor: not-allowed;
  opacity: 0.8;
}

.detected-location {
  padding: 0.75rem;
  border: 1px solid rgba(17, 64, 146, 0.18);
  border-radius: 0.35rem;
  background: rgba(17, 64, 146, 0.05);
  font-size: 0.9rem;
}

.detected-location__title {
  margin-bottom: 0.35rem;
  font-weight: 600;
  color: #114092;
}

.detected-location__content {
  line-height: 1.55;
}

// Transition for statistics card
.slide-fade-enter-active {
  transition: all 0.4s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-in;
}

.slide-fade-enter-from {
  transform: translateY(-20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

// Badge glow animation
@keyframes badge-glow {
  0%, 100% {
    box-shadow: 0 0 5px currentColor;
  }
  50% {
    box-shadow: 0 0 15px currentColor;
  }
}

.badge-glow {
  animation: badge-glow 2s ease-in-out infinite;
}
</style>
